import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  corrigerUnMessage,
  envoyerUnMessage,
  lireLAnnuaire,
  lireLeFil,
  poserLaLecture,
  poserLaNotification,
  poserUneReaction,
  retirerUnMessage,
} from '@groupe-trousseau/shared/api-client';
import { REACTIONS } from '@groupe-trousseau/shared/contracts';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ANNUAIRE ET LES CONVERSATIONS — `CDC-4` F-50, ET C'EST **UNE** SURFACE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **Une seule surface, pas deux.** Le produit ne met en relation que des personnes avec
 * qui on peut parler : **deux listes porteraient exactement les mêmes lignes.** » Et : « il
 * n'y a **pas deux inventaires de conversations** ».
 *
 * **C'est pourquoi cette feature lit `annuaire` et non `conversations`**, alors que les
 * deux routes existent. La première porte **la présence** en plus du dernier message ; la
 * seconde est ce que la bulle du chat consomme, sans présence. Les faire cohabiter sur le
 * même écran donnerait deux sources pour la même ligne, et **la ligne montrerait deux
 * états** selon la requête qui a répondu la dernière.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CETTE FEATURE N'EXPOSE PAS, ET CHAQUE ABSENCE EST UNE RÈGLE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Aucune fiche de personne.** « Une ligne, et rien derrière : **le produit ne montre
 *   jamais d'un tiers plus que son prénom, donc une fiche n'aurait rien à porter.** »
 * - **Aucun partage de dossier.** « Le partage ne part pas d'ici. Il part du dossier, où
 *   `CDC-3` l'a placé : **un second chemin ferait deux gestes pour une seule décision.** »
 * - **Aucune fermeture de relation.** Une personne qui veut fermer une relation « passe par
 *   la structure qui lui a donné son accès, **hors du produit** » (Q553).
 * - **Aucune cause quand une relation se ferme** : « l'écran ne dit **jamais** pourquoi ».
 *   La conversation disparaît simplement de la liste, et le serveur ne rend pas de motif.
 */

/**
 * **`actif` sert un second consommateur, et il vaut `true` par défaut** — ajouté le
 * 20260828 avec F-16. L'écran du cours a besoin de la liste des élèves du tuteur pour
 * en ajouter un, et **seulement quand ce geste s'ouvre** : c'est une requête de plus
 * sur la surface dont « la latence du trait est le critère numéro un ». Le motif et la
 * forme sont ceux de `useScene`, qui porte déjà ce drapeau.
 */
export function useAnnuaire(actif = true) {
  return useQuery({
    queryKey: CLES.annuaire,
    queryFn: () => deballer(lireLAnnuaire()),
    enabled: actif,
    // La présence porte trois valeurs et **rien ne s'en conserve** (F-48) : elle se relit,
    // elle ne se mémorise pas.
    staleTime: 15_000,
  });
}

/** Combien de messages par tranche. La pagination remonte le fil vers le passé. */
const PAR_TRANCHE = 50;

/**
 * **Le fil d'une conversation, paginé par curseur SUR LE RANG.**
 *
 * Le rang est unique par conversation, et c'est lui le curseur — pas un instant. Un
 * instant serait ambigu à la milliseconde près et **ferait sauter ou répéter un message**
 * sur deux envois simultanés.
 *
 * **La tranche la plus récente arrive en premier, et on remonte** : `avant` demande ce qui
 * précède. Une conversation se lit par sa fin.
 */
export function useFil(conversationId: string | null) {
  return useInfiniteQuery({
    queryKey: CLES.conversations.fil(conversationId ?? ''),
    enabled: conversationId !== null,
    queryFn: ({ pageParam }) =>
      deballer(
        lireLeFil({
          path: { id: conversationId ?? '' },
          query: {
            combien: PAR_TRANCHE,
            ...(pageParam === null ? {} : { avant: pageParam }),
          },
        }),
      ),
    initialPageParam: null as number | null,
    getNextPageParam: (derniere) => derniere.curseurSuivant,
  });
}

export function useEnvoyerUnMessage(conversationId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (texte: string) =>
      deballer(
        envoyerUnMessage({ path: { id: conversationId }, body: { texte } }),
      ),
    onSuccess: async () => {
      // **Envoyer touche DEUX surfaces** : le fil, et la ligne de l'annuaire qui porte le
      // dernier message. C'est le cas que la fabrique de clés existe pour rendre possible
      // — « l'invalidation traverse les features ».
      await cache.invalidateQueries({
        queryKey: CLES.conversations.fil(conversationId),
      });
      await cache.invalidateQueries({ queryKey: CLES.annuaire });
    },
  });
}

/**
 * **Poser la lecture — et le rang NE RECULE JAMAIS.**
 *
 * Le contrat l'écrit : « un rang inférieur à celui déjà posé est ignoré, **sans quoi des
 * non-lus ressusciteraient et l'indicateur de lecture de l'autre se retournerait** ». Le
 * front n'a donc aucune garde à écrire : il pose le rang du dernier message qu'il montre,
 * et le serveur arbitre.
 */
export function usePoserLaLecture(conversationId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (rang: number) =>
      deballer(
        poserLaLecture({ path: { id: conversationId }, body: { rang } }),
      ),
    onSuccess: () => cache.invalidateQueries({ queryKey: CLES.annuaire }),
  });
}

/**
 * **L'exception de notification, réglée DANS la conversation** — F-51.
 *
 * « L'exception se règle dans la conversation, **pas** dans les réglages », qui n'en
 * portent que le nombre. `null` retire l'exception et rend la conversation au réglage
 * général : c'est un troisième état, et il est nécessaire — sans lui, retirer une
 * exception obligerait à deviner le réglage général pour le recopier.
 */
export function usePoserLaNotification(conversationId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (notifier: boolean | null) =>
      deballer(
        poserLaNotification({ path: { id: conversationId }, body: { notifier } }),
      ),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: CLES.conversations.tout });
      // Le nombre d'exceptions vit sur l'écran des réglages : le changer ici le périme
      // là-bas, et deux nombres pour une même chose est exactement ce qu'on évite.
      await cache.invalidateQueries({ queryKey: CLES.reglages.tout });
    },
  });
}

/**
 * **Retirer un message laisse SA MARQUE, sans son contenu.**
 *
 * « L'autre a possiblement déjà lu, et **effacer sans trace laisserait réécrire ce qu'elle
 * a vu**, dans la seule surface du produit que personne ne peut auditer. » Le fil se
 * recharge donc plutôt que de retirer la ligne localement : la ligne reste, vidée.
 */
export function useRetirerUnMessage(conversationId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      deballer(retirerUnMessage({ path: { id: messageId } })),
    onSuccess: async () => {
      await cache.invalidateQueries({
        queryKey: CLES.conversations.fil(conversationId),
      });
      await cache.invalidateQueries({ queryKey: CLES.annuaire });
    },
  });
}

/**
 * **Corriger, c'est REMPLACER un texte, et le produit dit qu'il l'a été.**
 *
 * « **Correction : par son auteur seul, dans les cinq minutes qui suivent l'envoi**
 * (Q461). Passé ce délai, le texte ne se change plus. » Et : « la conversation montre
 * qu'un message a été corrigé, **sans montrer son texte d'avant**, et le texte d'avant
 * n'est pas conservé ».
 *
 * **DEUX SURFACES BOUGENT, ET C'EST LA MÊME RAISON QU'À L'ENVOI** : le fil porte le texte,
 * et la ligne de l'annuaire porte le dernier message. Corriger le dernier message sans
 * invalider l'annuaire y laisserait l'ancien texte — c'est-à-dire **exactement le texte
 * d'avant, que le produit s'engage à ne plus montrer nulle part**.
 *
 * **Le délai n'est pas gardé ici.** `estEncoreCorrigeable` ci-dessous ne sert qu'à ne pas
 * proposer un geste qui ne peut que refuser ; c'est le serveur qui arbitre, et il rend
 * `DELAI_DE_CORRECTION_DEPASSE`. Une garde de front qui ferait autorité mentirait dès que
 * l'horloge du poste dérive.
 */
export function useCorrigerUnMessage(conversationId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      texte,
    }: {
      readonly messageId: string;
      readonly texte: string;
    }) =>
      deballer(corrigerUnMessage({ path: { id: messageId }, body: { texte } })),
    onSuccess: async () => {
      await cache.invalidateQueries({
        queryKey: CLES.conversations.fil(conversationId),
      });
      await cache.invalidateQueries({ queryKey: CLES.annuaire });
    },
  });
}

/** Cinq minutes, en millisecondes — la fenêtre de correction de Q461. */
const DELAI_DE_CORRECTION_MS = 5 * 60 * 1000;

/**
 * **La fenêtre de correction est-elle encore ouverte ?**
 *
 * Elle vit dans la feature et non dans la page parce que **c'est une règle métier** : une
 * page « ne contient pas de logique métier », et cinq minutes est une décision du cahier
 * des charges, pas une décision d'affichage.
 *
 * **Le seuil est le même que celui du serveur, et l'inégalité aussi** : là-bas, `Date.now()
 * - createdAt > cinq minutes` refuse, donc l'égalité passe encore.
 */
export function estEncoreCorrigeable(
  instant: string,
  maintenant: number,
): boolean {
  return maintenant - new Date(instant).getTime() <= DELAI_DE_CORRECTION_MS;
}

/** La liste fermée, telle que le contrat la tient. Le front n'en invente aucune valeur. */
export type Reaction = (typeof REACTIONS)[number];

/**
 * **Poser une réaction — UNE par personne et par message, et le geste REMPLACE.**
 *
 * « **Les réactions existent** : **une par personne et par message**, choisie dans une
 * **liste fermée tenue par le produit**, jamais un texte libre » (DD-27). Le verbe HTTP est
 * `PUT` parce que le geste remplace : « une seconde réaction de la même personne prend la
 * place de la première au lieu de s'ajouter », et depuis D-051 c'est la contrainte d'unicité
 * de la base qui le garantit, pas une lecture préalable.
 *
 * **SEUL LE FIL EST INVALIDÉ, ET L'ABSENCE DE L'ANNUAIRE EST LA RÈGLE.** Une réaction ne
 * change pas le dernier message d'une conversation, donc la ligne de l'annuaire ne bouge
 * pas. L'invalider ferait une requête pour un écran qui rendrait le même octet — et
 * ferait croire, à qui lira ce fichier, qu'une réaction remonte dans la liste.
 *
 * **Aucun score, aucun badge, aucune série, aucun classement** : « aucun objet de score, de
 * badge, de série ni de classement n'existe », et « aucune action du chat ne laisse de
 * ligne » (Q478). Il n'y a donc rien d'autre à invalider, nulle part.
 */
export function usePoserUneReaction(conversationId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      valeur,
    }: {
      readonly messageId: string;
      readonly valeur: Reaction | null;
    }) =>
      deballer(
        poserUneReaction({
          path: { id: messageId },
          // **`valeur` est déclarée nullable au serveur et le client généré la rend NON
          // nullable** — c'est l'écart déjà constaté dans `features/cours/document.ts` :
          // `z.enum([...]).nullable()` sort bien `nullable: true` dans `openapi.json`, et
          // le générateur perd cette nullité **quand la propriété porte un `enum`**. Le
          // vide est pourtant le geste de retrait que le contrat écrit — « VIDE la
          // retire » —, donc ce corps est celui du contrat, et le transtypage ne rattrape
          // que la génération. **Le client n'est pas édité pour autant** : on régénère.
          body: { valeur } as { valeur: Reaction },
        }),
      ),
    onSuccess: () =>
      cache.invalidateQueries({
        queryKey: CLES.conversations.fil(conversationId),
      }),
  });
}
